import { useQueryClient } from '@tanstack/vue-query'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  changePosition,
  pauseTask as apiPause,
  removeTask as apiRemove,
  resumeTask as apiResume,
  retryTask as apiRetry,
  type ChangePositionPayload,
  type TaskCollection,
  type TaskInfo,
} from '@/api/tasks'
import {
  changeUri as apiChangeUri,
  removeDownloadResult as apiRemoveDownloadResult,
  type ChangeUriPayload,
} from '@/api/rpc'
import { useToast } from '@/composables/useToast'

// ---- v3.19.31 删除确认 (模块级共享单例) ----
// 所有删除入口 (卡片菜单/垃圾桶/键盘 Delete/详情页) 统一走一个确认弹窗:
// 任务将从【所有节点】同步删除, 可选同时删除已下载文件。
export interface TaskRemoveConfirmState {
  gid: string
  name: string
  force: boolean
}
const pendingRemoveConfirm = ref<TaskRemoveConfirmState | null>(null)
let removeResolve: ((deleted: boolean) => void) | null = null

export function useTaskActions() {
  const toast = useToast()
  const qc = useQueryClient()
  const { t } = useI18n()

  function invalidate(): void {
    void qc.invalidateQueries({ queryKey: ['tasks'] })
    void qc.invalidateQueries({ queryKey: ['task'] })
  }

  async function pause(task: TaskInfo): Promise<void> {
    try {
      await apiPause(task.gid)
      toast.success(t('tasks.toast.paused', { name: task.name ?? task.gid }))
      invalidate()
    } catch {
      /* toast already shown by api client */
    }
  }

  async function resume(task: TaskInfo): Promise<void> {
    try {
      await apiResume(task.gid)
      toast.success(t('tasks.toast.resumed', { name: task.name ?? task.gid }))
      invalidate()
    } catch {
      /* toast already shown by api client */
    }
  }

  // v3.19.31: remove 不再直接删 — 打开共享确认弹窗; 确认后全节点同步删。
  // 返回 Promise<boolean>: true = 已删除 (弹窗确认), false = 取消。
  async function remove(task: TaskInfo, force = false): Promise<boolean> {
    pendingRemoveConfirm.value = {
      gid: task.gid,
      name: task.name ?? task.gid,
      force,
    }
    return new Promise<boolean>((resolve) => {
      removeResolve = resolve
    })
  }

  async function confirmRemove(deleteFiles: boolean): Promise<void> {
    const p = pendingRemoveConfirm.value
    pendingRemoveConfirm.value = null
    removeResolve?.(true)
    removeResolve = null
    if (!p) return
    try {
      const res = await apiRemove(p.gid, p.force, deleteFiles)
      if (res && res.removed_on > 0) {
        toast.success(t('tasks.toast.removedCluster', { name: p.name, n: res.removed_on }))
      } else {
        toast.success(t('tasks.toast.removed', { name: p.name }))
      }
      invalidate()
    } catch {
      /* toast already shown by api client */
    }
  }

  function cancelRemove(): void {
    pendingRemoveConfirm.value = null
    removeResolve?.(false)
    removeResolve = null
  }

  // v3.19.31: 重试防抖 — 同一 gid 的重试请求在飞行中不再重复发出
  // (此前双击/自动重试每次都造一个新任务)。
  const retryingGids = new Set<string>()

  async function retry(task: TaskInfo): Promise<void> {
    if (retryingGids.has(task.gid)) return
    retryingGids.add(task.gid)
    try {
      const res = await apiRetry(task.gid)
      toast.success(
        t('tasks.toast.retried', {
          name: task.name ?? task.gid,
          gid: res.new_gid,
        }),
      )
      invalidate()
    } catch {
      /* toast already shown by api client */
    } finally {
      retryingGids.delete(task.gid)
    }
  }

  async function move(task: TaskInfo, pos: number, how: ChangePositionPayload['how'] = 'SET'): Promise<void> {
    try {
      await changePosition(task.gid, { pos, how })
      invalidate()
    } catch {
      /* toast already shown by api client */
    }
  }

  async function changeSource(
    task: TaskInfo,
    payload: ChangeUriPayload,
  ): Promise<void> {
    try {
      const res = await apiChangeUri(task.gid, payload)
      toast.success(
        t('tasks.toast.changed', {
          name: task.name ?? task.gid,
          added: res.addedCount,
          removed: res.removedCount,
        }),
      )
      invalidate()
    } catch {
      /* toast already shown by api client */
    }
  }

  async function removeResult(task: TaskInfo): Promise<void> {
    try {
      await apiRemoveDownloadResult(task.gid)
      toast.success(t('tasks.toast.removedResult', { name: task.name ?? task.gid }))
      invalidate()
    } catch {
      /* toast already shown by api client */
    }
  }

  function toggle(task: TaskInfo): void {
    if (task.status === 'paused') {
      void resume(task)
    } else {
      void pause(task)
    }
  }

  function withTasks<T>(cb: (collection: TaskCollection) => T): T {
    return cb('active')
  }

  // Cache key helpers — keep these stable so multiple views share state.
  const collectionKey = (kind: TaskCollection) => ['tasks', kind] as const
  const taskKey = (gid: string) => ['task', gid] as const

  const isAnyLoading = computed(() => {
    return qc.isFetching({ queryKey: ['tasks'] }) > 0
  })

  return {
    pause,
    resume,
    remove,
    confirmRemove,
    cancelRemove,
    removeConfirmState: pendingRemoveConfirm,
    retry,
    move,
    changeSource,
    removeResult,
    toggle,
    invalidate,
    withTasks,
    collectionKey,
    taskKey,
    isAnyLoading,
  }
}